import { Injectable } from '@angular/core';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { skip } from 'rxjs/operators';
import { SpaqFireDb } from './spaq-fire-db.service';

/**
 * Signs the visitor out and sends them home when the current org scope — read straight off the
 * URL's first hostname label (e.g. "cem" from cem.spaq.com) — gets set as RTDB's
 * /config/killSwitchScope (a single scope code, e.g. "CEM" — only one scope can be killed at a
 * time, not a list) while the app is already running. Never triggers when the visitor is on the
 * public domain (hostname starting with "public"), even if the derived scope matches.
 *
 * Same shape as MaintenanceScopeRedirectService, with two differences: the destination (home, not
 * an external site) and, crucially, `skip(1)` — RTDB's `.on('value')` always fires once
 * immediately with whatever's already there, and reacting to that value (not just to changes)
 * would redirect straight back into the same check on every reload of the home page, an infinite
 * loop, since — unlike the maintenance redirect — the destination is inside this same app. Only
 * a change made *after* the subscription starts (a Subject, not a BehaviorSubject) should act.
 *
 * This is a per-scope kill switch, distinct from KillSwitchService's existing per-app one (which
 * forces a reload of the current page on any change to a fixed RTDB timestamp path).
 *
 * Call `check()` once at app bootstrap (see AppComponent.ngOnInit) to start listening — it keeps
 * listening to /config/killSwitchScope for the life of the app (no take(1)/unsubscribe), so if
 * staff set the current scope while a tab is already open, that tab reacts immediately.
 */
@Injectable({ providedIn: 'root' })
export class KillSwitchScopeRedirectService {
	constructor(
		private db: SpaqFireDb,
		private afAuth: AngularFireAuth
	) {}

	check(): void {
		if (this.isPublicDomain()) {
			return;
		}

		const [scope] = window.location.hostname.split('.');
		if (!scope) {
			return;
		}

		const normalizedScope = scope.toLowerCase();
		this.db
			.killSwitchScope$()
			.pipe(skip(1)) // ignore whatever's already there at subscribe time — see class doc
			.subscribe({
				next: killedScope => {
					const isKilled = typeof killedScope === 'string' && killedScope.toLowerCase() === normalizedScope;
					if (isKilled) {
						this.logoutAndRedirectHome();
					}
				},
				// Fail-open — an RTDB hiccup here shouldn't block the whole app from loading.
				error: () => undefined
			});
	}

	/** Signs the visitor out (if logged in) before sending them home — same goOffline + signOut +
	 *  replace sequence as KillSwitchService.forceLogoutAndRefresh. signOut() is a harmless no-op
	 *  when nobody's logged in, and a signOut failure never blocks the redirect itself. */
	private logoutAndRedirectHome(): void {
		this.db.goOffline();
		this.afAuth
			.signOut()
			.catch(() => undefined)
			.finally(() => {
				window.location.replace('/');
			});
	}

	private isPublicDomain(): boolean {
		return window.location.hostname.toLowerCase().startsWith('public');
	}
}
